<template>
  <el-container class="main-container">
    <el-main class="data-container">
      <!-- 移动端视图保持不变 -->
      <div v-if="isMobile" class="mobile-view">
        <el-tabs v-model="radio1" stretch class="mobile-tabs" @tab-change="handlePageChange(1)">
          <el-tab-pane label="CN Server" name="CN Server"></el-tab-pane>
          <el-tab-pane label="US Server" name="US Server"></el-tab-pane>
          <el-tab-pane label="XK Server" name="XK Server"></el-tab-pane>
        </el-tabs>

        <section class="mobile-search-card">
          <div class="search-title">在线诊断日志</div>
          <div class="search-row">
            <span class="search-label">SN</span>
            <el-autocomplete
              v-model="serialNumber"
              :fetch-suggestions="(queryString, cb) => querySearch(queryString, cb, 'sn')"
              clearable
              class="mobile-search-input"
              placeholder="Please input sn"
              maxlength="12"
              @select="handleSelectSn"
              @keyup.enter="fetchData"
            />
            <el-button type="primary" plain class="mobile-search-btn" :loading="loading" @click="fetchData">
              查询
            </el-button>
          </div>

          <div class="search-row">
            <span class="search-label">VIN</span>
            <el-autocomplete
              v-model="myvin"
              :fetch-suggestions="(queryString, cb) => querySearch(queryString, cb, 'vin')"
              clearable
              class="mobile-search-input"
              placeholder="Please input vin"
              maxlength="17"
              @select="handleSelectVin"
              @keyup.enter="fetchDataVin"
            />
            <el-button type="primary" plain class="mobile-search-btn" :loading="loading" @click="fetchDataVin">
              查询
            </el-button>
          </div>

          <div class="search-row">
            <span class="search-label">时间</span>
            <el-date-picker
              v-model="logTimeRange"
              :shortcuts="logTimeShortcuts"
              popper-class="log-time-picker"
              type="datetimerange"
              value-format="YYYY-MM-DD HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              range-separator="至"
              :clearable="false"
              class="mobile-time-range"
            />
          </div>

          <div class="search-row search-row-actions">
            <el-button type="success" plain class="mobile-download-btn" @click="downloadExcel">
              下载 Excel
            </el-button>
          </div>
        </section>

        <section v-loading="loading" class="mobile-results">
          <div v-if="paginatedData.length > 0" class="mobile-card-list">
            <article v-for="row in paginatedData" :key="`${row.Sn || row.Vin || row.CreatedAt || 'log'}-${row.CreatedAt || row.Vin || row.Sn}`" class="data-card">
              <div class="card-header">
                <span class="card-title">{{ row.Sn || row.Vin || '诊断日志' }}</span>
                <el-tag :type="getServerTagType(radio1)" effect="light" size="small">{{ radio1 }}</el-tag>
              </div>

              <div class="card-content">
                <div
                  v-for="column in sortedVisibleColumns"
                  :key="column.prop"
                  class="info-row"
                  :class="{ 'full-width': shouldFullWidth(column.prop) }"
                >
                  <span class="label">{{ column.label }}</span>
                  <span class="value" :class="{ 'break-all': shouldBreakAll(column.prop) }">
                    <template v-if="getParamPrefixBadge(row[column.prop], column.prop)">
                      <span
                        class="param-prefix-icon"
                        :class="getParamPrefixBadge(row[column.prop], column.prop).className"
                        :title="getParamPrefixBadge(row[column.prop], column.prop).title"
                      >
                        {{ getParamPrefixBadge(row[column.prop], column.prop).label }}
                      </span>
                      <span>{{ formatValue(stripParamPrefix(row[column.prop], column.prop), column.prop) }}</span>
                    </template>
                    <template v-else>
                      {{ formatValue(row[column.prop], column.prop) }}
                    </template>
                  </span>
                </div>
              </div>
            </article>
          </div>

          <el-empty v-else class="empty-state" description="暂无在线诊断日志" />
        </section>

        <div v-if="total > pageSize" class="mobile-pagination">
          <el-pagination
            background
            small
            layout="prev, pager, next"
            :total="total"
            :page-size="pageSize"
            :current-page="currentPage"
            @current-change="handlePageChange"
          />
        </div>
      </div>

      <!-- 桌面端视图：提供组合字段和逐字段两种阅读方式。 -->
      <div v-else class="desktop-view">
        <div class="serial-number-container">
          <el-autocomplete v-model="serialNumber"
            :fetch-suggestions="(queryString, cb) => querySearch(queryString, cb, 'sn')" clearable
            class="inline-input w-210" placeholder="Please input sn" @select="handleSelectSn" maxlength="12"
            show-word-limit type="text" style="width: 210px" />
          <el-button borderType="outline" type="primary" plain @click="fetchData">提交</el-button>
          <el-autocomplete v-model="myvin" :fetch-suggestions="(queryString, cb) => querySearch(queryString, cb, 'vin')"
            clearable class="inline-input desktop-vin-input" placeholder="Please input vin" @select="handleSelectVin" maxlength="17"
            show-word-limit type="text" />
          <el-button borderType="outline" type="primary" plain @click="fetchDataVin">提交</el-button>
          <el-date-picker
            v-model="logTimeRange"
            :shortcuts="logTimeShortcuts"
            popper-class="log-time-picker"
            type="datetimerange"
            value-format="YYYY-MM-DD HH:mm:ss"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            range-separator="至"
            :clearable="false"
            class="desktop-time-range"
            style="width: 300px; flex: 0 0 300px;"
          />
          <el-button borderType="outline" type="success" plain @click="downloadExcel">下载 Excel</el-button>
        </div>

        <div class="log-view-toolbar">
        <el-tabs v-model="radio1" class="log-server-tabs">
          <el-tab-pane label="CN Server" name="CN Server"></el-tab-pane>
          <el-tab-pane label="US Server" name="US Server"></el-tab-pane>
          <el-tab-pane label="XK Server" name="XK Server"></el-tab-pane>
        </el-tabs>
        <div class="log-view-switch" role="group" aria-label="日志显示方式">
          <button :class="{ active: logViewMode === 'compact' }" :aria-pressed="logViewMode === 'compact'" @click="setLogViewMode('compact')">紧凑视图</button>
          <button :class="{ active: logViewMode === 'full' }" :aria-pressed="logViewMode === 'full'" @click="setLogViewMode('full')">完整视图</button>
        </div>
        </div>

        <div ref="resultsPanel" class="log-results-panel" :style="{ height: resultsHeight }">
          <LogCompactTable v-if="logViewMode === 'compact'" v-loading="loading" :rows="paginatedData" :columns="sortedVisibleColumns" />
          <el-table v-else :data="paginatedData" stripe border height="100%" style="width: 100%; flex: 1; min-height: 0;"
            v-loading="loading">
            <el-table-column v-for="column in sortedVisibleColumns" :key="column.prop" :prop="column.prop"
              :label="column.label" :width="column.width" header-align="center">
              <template #default="scope">
                <!-- 长内容列使用展开/收起功能 -->
                <div v-if="isLongContentColumn(column.prop)" style="position: relative;">
                  <div
                    :ref="el => setContentRef(el, scope.row, column.prop)"
                    :style="getContentStyle(scope.row, column.prop)">
                    <template v-if="getParamPrefixBadge(scope.row[column.prop], column.prop)">
                      <span
                        class="param-prefix-icon"
                        :class="getParamPrefixBadge(scope.row[column.prop], column.prop).className"
                        :title="getParamPrefixBadge(scope.row[column.prop], column.prop).title">
                        {{ getParamPrefixBadge(scope.row[column.prop], column.prop).label }}
                      </span>
                      <span>{{ formatCellDisplayValue(stripParamPrefix(scope.row[column.prop], column.prop), column.prop) }}</span>
                    </template>
                    <template v-else>
                      {{ formatCellDisplayValue(scope.row[column.prop], column.prop) }}
                    </template>
                  </div>
                  <el-button 
                    v-if="showMeasuredExpandButton(scope.row, column.prop)"
                    link 
                    type="primary" 
                    size="small"
                    style="padding: 0; margin-top: 4px; font-size: 12px;"
                    @click="toggleCellExpand(scope.row, column.prop)">
                    {{ isExpanded(scope.row, column.prop) ? '收起' : '展开更多' }}
                  </el-button>
                </div>
                <!-- 短内容正常显示，完全保持原样 -->
                <span v-else>{{ scope.row[column.prop] === '' ? '-' : (scope.row[column.prop] ?? '-') }}</span>
              </template>
            </el-table-column>
          </el-table>

          <div class="log-results-footer">
          <span>本次已加载 {{ total }} 条</span>
          <el-pagination background layout="prev, pager, next, sizes" :total="total"
            :current-page="currentPage" :page-size="pageSize" :page-sizes="[20, 50, 100]"
            @size-change="handlePageSizeChange" @current-change="handlePageChange">
          </el-pagination>
          </div>
        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, reactive, nextTick, watch } from 'vue';
import LogCompactTable from './LogCompactTable.vue';
import { useStore } from 'vuex';
import { getPageInfo, getPageInfoVin, Post_xk_setSomeOne } from '../api/users';
import { LOG_EXCEL_EXTENSION, LOG_EXCEL_MIME_TYPE, buildLogExcelFile } from '../utils/logExcelExport.mjs';
import { createDefaultLogTimeRange, createRecentLogTimeRange, formatLogDateTime } from '../utils/logTimeRange.mjs';
import { formatLogDisplayValue, splitLogParamPrefix } from '../utils/logTextFormat.mjs';
import { ElMessage } from 'element-plus';

const COLLAPSED_CONTENT_LINE_COUNT = 3;
const COLLAPSED_CONTENT_LINE_HEIGHT = 24;
const COLLAPSED_CONTENT_MAX_HEIGHT = `${COLLAPSED_CONTENT_LINE_COUNT * COLLAPSED_CONTENT_LINE_HEIGHT}px`;
const PARAM_PREFIX_BADGES = {
  sm2: { label: 'SM2', className: 'param-prefix-icon--sm2', title: 'SM2 加密参数' },
  pro: { label: 'PRO', className: 'param-prefix-icon--pro', title: 'PRO 参数' }
};

export default {
  components: { LogCompactTable },
  setup() {
    const store = useStore();
    
    // 用于跟踪展开状态的响应式对象
    const expandedCells = reactive({});
    const overflowCells = reactive({});
    const contentRefs = new Map();
    
    // 列配置保持不变
    const defaultColumns = [
      { prop: 'SnType', label: '用户类型', width: 65, isVisible: true, order: 1 },
      { prop: 'Version', label: '版本', width: 80, isVisible: true, order: 2 },
      { prop: 'Vin', label: 'Vin', width: 180, isVisible: true, order: 3 },
      { prop: 'DiagParam', label: '请求参数', width: 250, isVisible: true, order: 4 },
      { prop: 'ResData', label: '响应数据', width: 450, isVisible: true, order: 5 },
      { prop: 'FuncType', label: '功能类型', width: 60, isVisible: true, order: 6 },
      { prop: 'CreatedAt', label: '日志时间', width: 110, isVisible: true, order: 7 },
      { prop: 'DiagId', label: '软件ID', width: 120, isVisible: true, order: 8 },
      { prop: 'Sn', label: '序列号', width: 125, isVisible: true, order: 9 },
      { prop: 'runtime', label: '执行时间', width: 80, isVisible: true, order: 10 },
      { prop: 'Ip', label: 'Ip', width: 135, isVisible: true, order: 11 },
      { prop: 'Language', label: '语言', width: 60, isVisible: true, order: 12 },
      { prop: 'ProductType', label: '产品类型', width: 60, isVisible: true, order: 13 },
      { prop: 'LocalMac', label: 'LocalMac', width: 120, isVisible: true, order: 14 },
      { prop: 'WifiMac', label: 'WifiMac', width: 120, isVisible: true, order: 15 },
      { prop: 'WifiName', label: 'WifiName', width: 120, isVisible: true, order: 16 }
    ];
    
    const columns = computed({
      get: () => {
        return store.state.LogFormat && store.state.LogFormat.length > 0
          ? store.state.LogFormat
          : defaultColumns;
      }
    });

    const cityData = ref({
      'CN Server': [],
      'US Server': [],
      'XK Server': []
    });
    const queriedLog = ref({
      'CN Server': false,
      'US Server': false,
      'XK Server': false
    });

    const radio1 = ref('CN Server')
    const loading = ref(false)
    const serialNumber = ref('');
    const myvin = ref('');
    const logTimeRange = ref(createDefaultLogTimeRange());
    // 每次点击都按当前日期恢复，与页面初始时间范围保持一致。
    const logTimeShortcuts = [{
      text: '重置近一个月',
      value: () => createDefaultLogTimeRange().map(value => new Date(value.replace(' ', 'T'))),
    }];
    const currentPage = ref(1);
    const pageSize = ref(20);
    const logViewMode = ref('compact');
    const resultsPanel = ref(null);
    const resultsHeight = ref('calc(100dvh - 200px)');
    let resultsObserver;
    const total = computed(() => cityData.value[radio1.value].length);
    const isValidLength = ref(true);
    const recentQueries = ref(JSON.parse(localStorage.getItem('recentQueries')) || []);

    const isMobile = ref(window.innerWidth <= 768);

    // 按结果区实际位置保留底部空间，筛选栏换行时也能撑满窗口。
    const updateResultsHeight = () => {
      if (!resultsPanel.value) return;
      const top = resultsPanel.value.getBoundingClientRect().top + window.scrollY;
      resultsHeight.value = `${Math.max(200, window.innerHeight - top - 20)}px`;
    };
    const observeResultsLayout = () => {
      resultsObserver?.disconnect();
      if (!resultsPanel.value) return;
      updateResultsHeight();
      resultsObserver = new ResizeObserver(updateResultsHeight);
      resultsObserver.observe(resultsPanel.value.parentElement);
    };
    watch(isMobile, () => nextTick(observeResultsLayout));

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
      currentPage.value = 1;
      measureOverflowCells();
      nextTick(updateResultsHeight);
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      nextTick(observeResultsLayout);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      resultsObserver?.disconnect();
    });

    const paginatedData = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = currentPage.value * pageSize.value;
      return cityData.value[radio1.value].slice(start, end);
    });

    const handlePageChange = (page) => {
      currentPage.value = page;
      // 切换页面时清空展开状态
      resetCellDisplayState();
      nextTick(measureOverflowCells);
    };

    const handlePageSizeChange = (size) => {
      pageSize.value = size;
      handlePageChange(1);
    };
    const setLogViewMode = (mode) => {
      logViewMode.value = mode;
      resetCellDisplayState();
      nextTick(measureOverflowCells);
    };
    watch(radio1, () => handlePageChange(1));

    // ========== 展开/收起相关函数 ==========
    
    // 判断是否为需要展开功能的长内容列
    const isLongContentColumn = (prop) => {
      return ['DiagParam', 'ResData'].includes(prop);
    };

    // 判断是否显示展开按钮（内容长度超过阈值）
    const showExpandButton = (value) => {
      if (!value) return false;
      const str = String(value);
      return str.length > 180; // 超过100个字符显示展开按钮
    };

    const getParamPrefixKey = (value, prop) => {
      if (prop !== 'DiagParam' || typeof value !== 'string') return null;
      return splitLogParamPrefix(value, prop).prefix || null;
    };

    const getParamPrefixBadge = (value, prop) => {
      const prefix = getParamPrefixKey(value, prop);
      return prefix ? PARAM_PREFIX_BADGES[prefix] : null;
    };

    const stripParamPrefix = (value, prop) => {
      return splitLogParamPrefix(value, prop).content;
    };

    const formatCellDisplayValue = (value, prop) => {
      const formatted = formatLogDisplayValue(value, prop);
      return formatted || '-';
    };

    // 获取单元格的唯一标识
    const getCellKey = (row, prop) => {
      const rowIndex = cityData.value[radio1.value].indexOf(row);
      return `${rowIndex}_${prop}`;
    };

    const resetCellDisplayState = () => {
      Object.keys(expandedCells).forEach(key => delete expandedCells[key]);
      Object.keys(overflowCells).forEach(key => delete overflowCells[key]);
      contentRefs.clear();
    };

    const measureCellOverflow = (row, prop, el) => {
      if (!el || !isLongContentColumn(prop) || isExpanded(row, prop)) return;
      const key = getCellKey(row, prop);
      if (key.startsWith('-1_')) return;
      overflowCells[key] = el.scrollHeight > el.clientHeight + 1;
    };

    const measureOverflowCells = () => {
      nextTick(() => {
        contentRefs.forEach(({ el, row, prop }, key) => {
          if (!document.body.contains(el)) {
            contentRefs.delete(key);
            delete overflowCells[key];
            return;
          }
          measureCellOverflow(row, prop, el);
        });
      });
    };

    const setContentRef = (el, row, prop) => {
      const key = getCellKey(row, prop);
      if (!el || key.startsWith('-1_')) {
        contentRefs.delete(key);
        return;
      }
      contentRefs.set(key, { el, row, prop });
      measureCellOverflow(row, prop, el);
    };

    const showMeasuredExpandButton = (row, prop) => {
      if (!row || !row[prop]) return false;
      const key = getCellKey(row, prop);
      return Boolean(overflowCells[key] || isExpanded(row, prop));
    };

    // 判断单元格是否展开
    const isExpanded = (row, prop) => {
      const key = getCellKey(row, prop);
      return expandedCells[key] || false;
    };

    // 切换展开/收起状态
    const toggleCellExpand = (row, prop) => {
      const key = getCellKey(row, prop);
      expandedCells[key] = !expandedCells[key];
      if (!expandedCells[key]) {
        nextTick(measureOverflowCells);
      }
    };

    // 获取内容样式
    const getContentStyle = (row, prop) => {
      if (isExpanded(row, prop)) {
        return {
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap'
        };
      }
      return {
        maxHeight: COLLAPSED_CONTENT_MAX_HEIGHT,
        overflow: 'hidden',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap'
      };
    };

    // 原有函数保持不变
    const fetchData = async () => {
      const serialNumberValue = serialNumber.value.trim();
      const useLatestLogs = hasNoSearchInput();
      if (!useLatestLogs && serialNumberValue.length !== 12) {
        ElMessage.error('请输入正确的序列号!');
        return;
      }
      const queryValue = useLatestLogs ? 'all' : serialNumberValue;
      const useBaseURL = (radio1.value === 'CN Server') ? true : false;
      const timeParams = useLatestLogs ? useRecentLogTimeRange() : getLogTimeParams();
      loading.value = true;
      try {
        let response;
        if (radio1.value === 'XK Server') {
          const limit = 100;
          const form = { pass: "wonihaoma" };
          response = await Post_xk_setSomeOne(buildXkLogUrl('setlog/getpagelogXK', { limit, sn: queryValue, ...timeParams }), form);
        }
        else {
          response = await getPageInfo(100, queryValue, useBaseURL, timeParams);
        }
        columns.value = response.data.columns;
        cityData.value[radio1.value] = response.data.tableData;
        queriedLog.value[radio1.value] = true;
        if (serialNumberValue.length === 12) {
          updateRecentQueries("sn", serialNumberValue);
        }
        currentPage.value = 1;
        resetCellDisplayState();
        nextTick(measureOverflowCells);
      } catch (error) {
        ElMessage.error('请求失败，请重试');
      } finally {
        loading.value = false;
      }
    };

    const fetchDataVin = async () => {
      const myvinValue = myvin.value.trim();
      const useBaseURL = (radio1.value === 'CN Server') ? true : false;
      const useLatestLogs = hasNoSearchInput();
      if (!useLatestLogs && !myvinValue) {
        ElMessage.error('请输入VIN');
        return;
      }
      const queryValue = useLatestLogs ? 'all' : myvinValue;
      if (myvinValue) {
        updateRecentQueries("vin", myvinValue);
      }
      const timeParams = useLatestLogs ? useRecentLogTimeRange() : getLogTimeParams();
      loading.value = true;
      try {
        let response;
        if (radio1.value === 'XK Server') {
          const limit = 100;
          const form = { pass: "wonihaoma" };
          response = await Post_xk_setSomeOne(buildXkLogUrl('setlog/getpagelogvinXK', { limit, vin: queryValue, ...timeParams }), form);
        }
        else {
          response = await getPageInfoVin(100, queryValue, useBaseURL, timeParams);
        }
        columns.value = response.data.columns;
        cityData.value[radio1.value] = response.data.tableData;
        queriedLog.value[radio1.value] = true;
        currentPage.value = 1;
        resetCellDisplayState();
        nextTick(measureOverflowCells);
      } catch (error) {
        ElMessage.error('请求失败，请重试');
      } finally {
        loading.value = false;
      }
    };

    const getLogTimeParams = () => {
      const [startTime, endTime] = logTimeRange.value || [];
      return { startTime, endTime };
    };

    const hasNoSearchInput = () => !serialNumber.value.trim() && !myvin.value.trim();

    const useRecentLogTimeRange = () => {
      logTimeRange.value = createRecentLogTimeRange();
      return getLogTimeParams();
    };

    const buildXkLogUrl = (path, params) => {
      const query = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        query.set(key, value);
      });
      return `${path}?${query.toString()}`;
    };

    const downloadExcel = () => {
      const rows = cityData.value[radio1.value] || [];
      if (!queriedLog.value[radio1.value]) {
        ElMessage.warning('先查询日志，才能生成Excel数据');
        return;
      }
      if (rows.length === 0) {
        ElMessage.warning('没有可下载的日志内容');
        return;
      }

      const visibleColumns = sortedVisibleColumns.value;
      const workbookBytes = buildLogExcelFile(visibleColumns, rows);
      const blob = new Blob([workbookBytes], { type: LOG_EXCEL_MIME_TYPE });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = formatLogDateTime(new Date()).replace(/[-: ]/g, '');
      link.href = url;
      link.download = `online-diagnostic-log-${radio1.value.replace(/\s+/g, '-')}-${stamp}.${LOG_EXCEL_EXTENSION}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      ElMessage.success(`已生成 ${rows.length} 条日志`);
    };

    const updateRecentQueries = (type, query) => {
      const index = recentQueries.value.findIndex(item => item.value === query);
      if (index !== -1) {
        recentQueries.value.splice(index, 1);
      }
      recentQueries.value.unshift({ type: type, value: query });
      if (recentQueries.value.length > 5) {
        recentQueries.value.pop();
      }
      localStorage.setItem('recentQueries', JSON.stringify(recentQueries.value));
    };

    const handleSelectSn = (item) => {
      updateRecentQueries("sn", item.value);
    };
    
    const handleSelectVin = (item) => {
      updateRecentQueries("vin", item.value);
    };

    const sortedVisibleColumns = computed(() => {
      return columns.value
        .filter(column => column.isVisible)
        .sort((a, b) => a.order - b.order);
    });
    
    const querySearch = (queryString, cb, type) => {
      const suggestions = recentQueries.value.filter(query => query.type === type);
      cb(suggestions);
    };

    const getServerTagType = (server) => {
      const types = {
        'CN Server': 'success',
        'US Server': 'info',
        'XK Server': 'warning'
      };
      return types[server] || 'primary';
    };

    const shouldFullWidth = (prop) => {
      return ['DiagParam', 'ResData'].includes(prop);
    };

    const shouldBreakAll = (prop) => {
      return ['DiagParam', 'ResData', 'Vin'].includes(prop);
    };

    const formatValue = (value, prop) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'string' && value.length === 0) return '-';
      const formatted = formatLogDisplayValue(value, prop);
      
      if (prop === 'ResData' && formatted.length > 100) {
        return formatted.substring(0, 100) + '...';
      }
      if (prop === 'DiagParam' && formatted.length > 50) {
        return formatted.substring(0, 50) + '...';
      }
      return formatted || '-';
    };

    return {
      columns,
      currentPage,
      pageSize,
      logViewMode,
      resultsPanel,
      resultsHeight,
      setLogViewMode,
      handlePageSizeChange,
      total,
      paginatedData,
      serialNumber,
      myvin,
      logTimeRange,
      logTimeShortcuts,
      isValidLength,
      handlePageChange,
      fetchData,
      fetchDataVin,
      loading,
      radio1,
      cityData,
      querySearch,
      handleSelectSn,
      handleSelectVin,
      sortedVisibleColumns,
      isMobile,
      getServerTagType,
      shouldFullWidth,
      shouldBreakAll,
      formatValue,
      // 展开/收起相关
      isLongContentColumn,
      showMeasuredExpandButton,
      setContentRef,
      getParamPrefixBadge,
      stripParamPrefix,
      formatCellDisplayValue,
      isExpanded,
      toggleCellExpand,
      getContentStyle,
      downloadExcel
    };
  },
};  
</script>

<style>
/* 2.8 版没有 footer 插槽，将本页快捷项排到确定按钮旁。 */
.log-time-picker .el-date-range-picker.has-sidebar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  width: 646px;
}

.log-time-picker .el-picker-panel__body-wrapper {
  display: contents;
}

.log-time-picker .has-sidebar .el-picker-panel__body {
  order: 0;
  width: 100%;
  margin-left: 0;
  border-bottom: 1px solid var(--el-border-color-light);
}

.log-time-picker .el-picker-panel__sidebar {
  order: 1;
  position: static;
  width: auto;
  padding: 0;
  border: 0;
}

.log-time-picker .el-picker-panel__shortcut {
  width: auto;
  padding: 0 10px;
  line-height: 24px;
  color: var(--el-color-primary);
}

.log-time-picker .el-picker-panel__footer {
  order: 2;
  border-top: 0;
}

@media (max-width: 768px) {
  .log-time-picker .el-date-range-picker.has-sidebar {
    width: calc(100vw - 24px);
  }

  .log-time-picker .has-sidebar .el-picker-panel__body {
    min-width: 0;
  }

  .log-time-picker .el-date-range-picker__content {
    padding: 8px 4px;
  }

  .log-time-picker .el-date-range-picker__header div {
    margin: 0 28px;
    font-size: 12px;
  }
}
</style>

<style scoped>
/* 保持原有所有样式不变 */
.main-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.header-container {
  text-align: center;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.el-table {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.el-table th,
.el-table td {
  text-align: left;
  padding: 12px 20px;
}

.serial-number-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}

.desktop-time-range {
  flex: 0 0 300px;
  width: 300px !important;
}

.data-container {
  padding-top: 0px;
}

.param-prefix-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 18px;
  margin-right: 4px;
  padding: 0 4px;
  border-radius: 4px;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: 1px;
}

.param-prefix-icon--sm2 {
  background: #67c23a;
}

.param-prefix-icon--pro {
  background: #409eff;
}

::v-deep .el-tabs__header {
  margin-bottom: 0px !important;
}

/* 移动端样式保持不变 */
@media screen and (max-width: 768px) {
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
    padding: 12px;
    background-color: #f5f7fa;
    min-height: 100vh;
  }

  .mobile-tabs {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 8px 8px 0;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .mobile-tabs :deep(.el-tabs__item) {
    font-size: 14px;
    padding: 0 8px;
  }

  .mobile-tabs :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
  }

  .mobile-search-card {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .search-title {
    font-size: 14px;
    font-weight: 500;
    color: #409eff;
    margin-bottom: 16px;
    padding-left: 4px;
    border-left: 3px solid #409eff;
  }

  .search-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .search-row:last-child {
    margin-bottom: 0;
  }

  .search-label {
    width: 40px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;
    text-align: right;
  }

  .mobile-search-input {
    flex: 1;
  }

  .mobile-time-range {
    flex: 1;
    min-width: 0;
  }

  .search-row-actions {
    justify-content: flex-end;
  }

  .mobile-download-btn {
    flex: 1;
    height: 36px;
    border-radius: 8px;
  }

  .mobile-search-input :deep(.el-input__wrapper) {
    border-radius: 8px;
    background-color: #f5f7fa;
  }

  .mobile-search-btn {
    width: 60px;
    border-radius: 8px;
    height: 36px;
    font-size: 13px;
    padding: 0;
  }

  .mobile-card-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  .data-card {
    background: linear-gradient(145deg, #ffffff 0%, #fafafa 100%);
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.8);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #edf0f5;
  }

  .card-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  .card-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    font-size: 13px;
    line-height: 1.5;
  }

  .info-row.full-width {
    flex-direction: column;
  }

  .info-row.full-width .label {
    width: 100%;
    margin-bottom: 4px;
    color: #909399;
  }

  .info-row.full-width .value {
    width: 100%;
    background-color: #f5f7fa;
    padding: 6px 8px;
    border-radius: 6px;
    font-family: monospace;
    font-size: 12px;
  }

  .label {
    width: 80px;
    color: #909399;
    flex-shrink: 0;
  }

  .value {
    color: #303133;
    word-break: break-word;
    flex: 1;
  }

  .value.break-all {
    word-break: break-all;
  }

  .empty-state {
    background-color: #ffffff;
    border-radius: 16px;
    padding: 32px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  }

  .mobile-pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding: 12px;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .mobile-pagination :deep(.el-pagination) {
    --el-pagination-button-width: 32px;
    --el-pagination-button-height: 32px;
  }

  .mobile-pagination :deep(.el-pagination button),
  .mobile-pagination :deep(.el-pagination span:not(.el-pagination__total)) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
  }

  :global(html.dark .mobile-view) {
    background-color: var(--wf-page-bg);
  }

  :global(html.dark .mobile-tabs),
  :global(html.dark .mobile-search-card),
  :global(html.dark .data-card),
  :global(html.dark .empty-state),
  :global(html.dark .mobile-pagination) {
    border-color: var(--wf-border);
    background: var(--wf-surface);
    color: var(--wf-text-primary);
    box-shadow: 0 4px 14px var(--wf-shadow);
  }

  :global(html.dark .mobile-search-input .el-input__wrapper),
  :global(html.dark .info-row.full-width .value) {
    background-color: var(--wf-surface-soft);
  }

  :global(html.dark .search-label),
  :global(html.dark .label) {
    color: var(--wf-text-muted);
  }

  :global(html.dark .card-title),
  :global(html.dark .value) {
    color: var(--wf-text-regular);
  }

  :global(html.dark .card-header) {
    border-bottom-color: var(--wf-border);
  }
}

.log-view-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 4px; }
.log-server-tabs { min-width: 0; flex: 1; }
.log-server-tabs :deep(.el-tabs__content) { display: none; }
.log-server-tabs :deep(.el-tabs__nav-wrap::after) { height: 1px; opacity: 0.45; }
.log-view-switch { display: inline-flex; border: 1px solid var(--el-border-color); border-radius: 3px; overflow: hidden; flex-shrink: 0; }
.log-view-switch button { padding: 6px 14px; border: 0; background: var(--el-bg-color); color: var(--el-text-color-secondary); font: 12px/18px 'Microsoft YaHei', sans-serif; cursor: pointer; }
.log-view-switch button.active { background: var(--el-color-primary); color: #fff; }
.log-view-switch button:focus-visible { outline: 2px solid var(--el-color-primary); outline-offset: -2px; }
.log-results-panel { display: flex; flex-direction: column; min-height: 200px; }
.log-results-footer { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 16px; padding-top: 12px; color: var(--el-text-color-secondary); font-size: 12px; }

.serial-number-container :deep(.desktop-vin-input) {
  width: 300px;
  flex: 0 0 300px;
}

@media screen and (min-width: 769px) {
  .mobile-view {
    display: none;
  }
}
</style>
